import { useEffect, Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { Badge } from 'react-bootstrap';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { formatNumber } from '../../utils/generalFunctions';

import { editarBreadcrumb } from '../../actions/breadcrumb';
import { obtenerMembresia, limpiarMembresia, obtenerPagosMembresia } from '../../actions/membresia';
import { obtenerMetodoPago } from '../../actions/configuracion';

import GestionarMembresia from './GestionarMembresia';

const Membresia = () => {

    let rutasBread = [
        {
            activo: true,
            nombre: 'Membresía',
            path: '/membresia'
        }
    ];

    const dispatch = useDispatch();

    const { usuario } = useSelector(state => state.auth);
    const { listado, detalles, pagos } = useSelector(state => state.membresia);
    const { metodo_pago } = useSelector(state => state.configuracion);

    /* Membresía */
    const [membresiaData, setMembresiaData] = useState({
        indicadores_generales: {
            fecha_inicio: null,
            fecha_fin: null,
            fecha_renovacion: null,
            beneficios: 0,
            membresia_actual: null
        },
        actual: {
            _id: null,
            id_membresia: null,
            nombre: null,
            status: null,
            beneficios: []
        }
    });

    const [membresiasData, setMembresiasData] = useState([]);
    const [pagosData, setPagosData] = useState([]);
    const [membresiaActual, setMembresiaActual] = useState(null);

    const { indicadores_generales, actual } = membresiaData;

    const paginationOptions = { rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' };

    /* Columnas de historial de contrataciones */
    const contratacionesColumnas = [
        {
            name: 'Membresía',
            sortable: true,
            selector: row => row.membresia?.nombre || 'Sin definir'
        },
        {
            name: 'Total anual',
            selector: row => row.total === 0 ? 'Gratuita' : formatNumber(row.total, 2, '$ ', '') + ' IVA inc.'
        },
        {
            name: 'Periodo',
            sortable: true,
            selector: row => handleRangoFechas(row.fecha_inicio, row.fecha_fin),
            grow: 3
        },
        {
            name: 'Estado',
            sortable: true,
            selector: row => {
                switch(row.status){
                    case 0:
                        return <h5><Badge pill bg="success">Activa</Badge></h5>;
                    case 1:
                        return <h5><Badge pill bg="warning">Periodo de gracia</Badge></h5>;
                    case 2:
                        return <h5><Badge pill bg="danger">Cancelada / Vencida</Badge></h5>;
                    default:
                        return <h5><Badge pill bg="secondary">Sin definir</Badge></h5>;
                }
            }
        }
    ];

    /* Columnas de historial de pagos */
    const pagosColumnas = [
        {
            name: 'Fecha',
            sortable: true,
            selector: row => row.fecha ? new Date(row.fecha).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'
        },
        {
            name: 'Tarjeta',
            selector: row => row.digitos ? `${row.marca ? row.marca.toUpperCase() + ' ' : ''}•••• ${row.digitos}` : '—'
        },
        {
            name: 'Total',
            selector: row => row.total === 0 ? 'Gratuita' : formatNumber(row.total, 2, '$ ', '') + ' IVA inc.'
        },
        {
            name: 'Estado',
            selector: row => {
                switch(row.status){
                    case 2:
                        return <h5><Badge pill bg="success">Pagado</Badge></h5>;
                    case 3:
                        return <h5><Badge pill bg="danger">Fallido</Badge></h5>;
                    case 4:
                        return <h5><Badge pill bg="secondary">Cancelado</Badge></h5>;
                    default:
                        return <h5><Badge pill bg="warning">Pendiente</Badge></h5>;
                }
            }
        }
    ];

    /* Beneficios */
    const beneficiosColumnas = [
        {
            name: 'Beneficio',
            sortable: true,
            selector: row => row || 'Sin definir'
        }
    ];

    /* Funciones generales */
    const handleRangoFechas = (inicio, fin) => {
        if(inicio && fin){
            const diferencia = new Date(fin).getTime() - new Date(inicio).getTime();
            const total_dias = Math.ceil(diferencia / (1000 * 3600 * 24));
            const fmt = d => new Date(d).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
            return total_dias <= 1
                ? 'Únicamente el ' + fmt(inicio)
                : 'Del ' + fmt(inicio) + ' al ' + fmt(fin);
        }
        if(inicio){
            return 'A partir del ' + new Date(inicio).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
        }
        return '—';
    };

    const handleStatus = (membresia) => {
        switch(membresia?.status){
            case 0:
                return <h3 className="text-muted"><Badge pill bg="success">{membresia?.nombre}</Badge></h3>;
            case 1:
                return <h3 className="text-muted"><Badge pill bg="warning">Periodo de gracia</Badge></h3>;
            case 2:
                return <h3 className="text-muted"><Badge pill bg="danger">Cancelada / Vencida</Badge></h3>;
            default:
                return <h3 className="text-muted"><Badge pill bg="secondary">Sin membresía</Badge></h3>;
        }
    };

    const handleGestionarMembresia = (membresia) => {
        if(usuario?.permisos?.membresia?.gestionar) {
            if(membresia) {
                const contratacion = membresiasData.find(c => String(c._id) === String(membresia._id));
                setMembresiaActual({ ...membresia, stripe_price_id: contratacion?.membresia?.stripe_price_id ?? null });
            } else {
                setMembresiaActual(null);
            }
        }
    };

    /* Effects */
    useEffect(() => {
        dispatch(obtenerMembresia());
        dispatch(obtenerMetodoPago());
        dispatch(obtenerPagosMembresia());

        return () => {
            dispatch(limpiarMembresia());
        };
    }, []);

    useEffect(() => {
        if(detalles){
            setMembresiaData({
                ...membresiaData,
                indicadores_generales: {
                    fecha_inicio: detalles.fecha_inicio,
                    fecha_fin: detalles.fecha_fin,
                    fecha_renovacion: detalles.fecha_renovacion,
                    beneficios: detalles.beneficios?.length || 0,
                    membresia_actual: detalles
                },
                actual: detalles
            });
        }
    }, [detalles]);

    useEffect(() => {
        setMembresiasData(listado || []);
    }, [listado]);

    useEffect(() => {
        setPagosData(pagos || []);
    }, [pagos]);

    useEffect(() => {
        dispatch(editarBreadcrumb(rutasBread));
    }, [rutasBread]);

    return (
        <Fragment>
            <HelmetProvider>
                <Helmet>
                    <style>{`
                        .btn-badge-status {
                            background-color: #fff;
                            border: none;
                            outline: none;
                            padding: 0;
                        }
                        .valor_indicador {
                            font-size: 25px;
                            margin: 0;
                        }
                        .descripcion_indicador {
                            margin: 0;
                        }
                        .tarjeta-card {
                            background: linear-gradient(135deg, #111F62 0%, #21B4E9 100%);
                            border-radius: 12px;
                            color: #fff;
                            padding: 20px 24px;
                        }
                    `}</style>
                </Helmet>
            </HelmetProvider>

            <GestionarMembresia membresia={membresiaActual} reestablecerMembresia={handleGestionarMembresia} />

            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="title-section my-5">Información de membresía</h3>

                    {/* Indicadores */}
                    <div className="row mb-4">
                        {
                            (indicadores_generales?.membresia_actual?.status === 0 || indicadores_generales?.membresia_actual?.status === 1) &&
                                <Fragment>
                                    <div className="col-md text-center">
                                        <p className="valor_indicador subtitle-section">
                                            { handleRangoFechas(indicadores_generales?.fecha_inicio, indicadores_generales?.fecha_fin) }
                                        </p>
                                        <p className="descripcion_indicador subtitle-section">Periodo de contratación</p>
                                    </div>
                                    <div className="col-md text-center">
                                        <p className="valor_indicador subtitle-section">
                                            { indicadores_generales?.fecha_renovacion
                                                ? new Date(indicadores_generales.fecha_renovacion).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })
                                                : '—'
                                            }
                                        </p>
                                        <p className="descripcion_indicador subtitle-section">
                                            { indicadores_generales?.membresia_actual?.cancel_at_period_end
                                                ? <span className="text-warning"><i className="fa-solid fa-ban me-1"></i>Vencimiento (sin renovación)</span>
                                                : 'Próxima renovación'
                                            }
                                        </p>
                                    </div>
                                    {
                                        indicadores_generales?.membresia_actual?.limite_asesorias != null &&
                                        <div className="col-md-2 text-center">
                                            <p className="valor_indicador subtitle-section">
                                                {indicadores_generales.membresia_actual.asesorias_restantes}
                                                <span style={{ fontSize: '0.6em', fontWeight: 'normal' }}> / {indicadores_generales.membresia_actual.limite_asesorias}</span>
                                            </p>
                                            <p className="descripcion_indicador subtitle-section">Asesorías disponibles este mes</p>
                                        </div>
                                    }
                                </Fragment>
                        }
                        <div className={
                            (indicadores_generales?.membresia_actual?.status === 0 || indicadores_generales?.membresia_actual?.status === 1 ? '' : 'offset-md-10 ') +
                            'col-md-2 text-center'
                        }>
                            <div className="mt-2 d-flex justify-content-center">
                                <button className="btn-badge-status" onClick={() => handleGestionarMembresia(indicadores_generales?.membresia_actual)}>
                                    { handleStatus(indicadores_generales?.membresia_actual) }
                                </button>
                            </div>
                            <p className="descripcion_indicador subtitle-section">Membresía</p>
                        </div>
                    </div>

                    {/* CTA gestionar membresía */}
                    {usuario?.permisos?.membresia?.gestionar && (() => {
                        const status = indicadores_generales?.membresia_actual?.status;
                        const isActiva = status === 0;
                        const isGracia = status === 1;
                        const gradient = isActiva
                            ? 'linear-gradient(135deg, #0d6958 0%, #0CB394 100%)'
                            : isGracia
                                ? 'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)'
                                : 'linear-gradient(135deg, #111F62 0%, #2F328A 100%)';

                        return (
                            <div
                                className="d-flex align-items-center justify-content-between rounded-3 px-4 py-4 mb-4"
                                style={{ background: gradient, boxShadow: '0 4px 18px rgba(0,0,0,0.15)' }}
                            >
                                <div className="text-white">
                                    <p className="mb-1 fw-bold" style={{ fontSize: '1rem', opacity: 0.85, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                        {isActiva ? 'Plan activo' : isGracia ? 'Periodo de gracia' : 'Sin membresía'}
                                    </p>
                                    <h5 className="mb-1 fw-bold text-white">
                                        {isActiva
                                            ? indicadores_generales.membresia_actual.nombre
                                            : isGracia
                                                ? 'Tu membresía está por vencer'
                                                : 'Comienza hoy'
                                        }
                                    </h5>
                                    <p className="mb-0" style={{ fontSize: '0.85rem', opacity: 0.85 }}>
                                        {isActiva && indicadores_generales.membresia_actual.cancel_at_period_end
                                            ? 'La renovación automática está desactivada. Tu acceso vence al final del periodo activo.'
                                            : isActiva
                                                ? 'Puedes cancelar o mejorar tu plan en cualquier momento'
                                                : isGracia
                                                    ? 'Renueva antes de que expire el periodo de gracia'
                                                    : 'Contrata una membresía y accede a todos los beneficios'
                                        }
                                    </p>
                                </div>
                                <button
                                    className="btn btn-light fw-semibold flex-shrink-0 ms-4 px-4"
                                    style={{ borderRadius: '8px', color: isActiva ? '#0d6958' : isGracia ? '#b45309' : '#111F62' }}
                                    onClick={() => handleGestionarMembresia(indicadores_generales?.membresia_actual)}
                                >
                                    <i className={`fa-solid ${isActiva ? 'fa-gear' : isGracia ? 'fa-rotate' : 'fa-bolt'} me-2`}></i>
                                    {isActiva ? 'Administrar' : isGracia ? 'Renovar' : 'Contratar'}
                                </button>
                            </div>
                        );
                    })()}

                    {/* Acordeón */}
                    <div className="row">
                        <div className="col-md-12">
                            <div className="accordion" id="accordionDetalles">

                                {/* Historial de contrataciones */}
                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingOne">
                                        <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="true" aria-controls="collapseOne">
                                            Historial de membresías
                                        </button>
                                    </h2>
                                    <div id="collapseOne" className="accordion-collapse collapse show" aria-labelledby="headingOne" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body row">
                                            <DataTable
                                                columns={contratacionesColumnas}
                                                data={membresiasData}
                                                noDataComponent="No hay contrataciones registradas"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Historial de pagos */}
                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingPagos">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapsePagos" aria-expanded="false" aria-controls="collapsePagos">
                                            Historial de pagos
                                        </button>
                                    </h2>
                                    <div id="collapsePagos" className="accordion-collapse collapse" aria-labelledby="headingPagos" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body row">
                                            <DataTable
                                                columns={pagosColumnas}
                                                data={pagosData}
                                                noDataComponent="No hay pagos registrados"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Beneficios */}
                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingTwo">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo" aria-expanded="false" aria-controls="collapseTwo">
                                            Beneficios adquiridos
                                        </button>
                                    </h2>
                                    <div id="collapseTwo" className="accordion-collapse collapse" aria-labelledby="headingTwo" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body">
                                            <DataTable
                                                columns={beneficiosColumnas}
                                                data={actual?.beneficios || []}
                                                noDataComponent="No hay beneficios por el momento, adquiere una membresía"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default Membresia;
